import React, { useState, useEffect, useRef, useMemo } from 'react'; // 添加useMemo优化
import { 
  Box, 
  Typography, 
  TextField,
  IconButton,
  InputAdornment,
  Paper,
  Button,
  styled,
  useTheme,
  Slider,
  Avatar,
  Fade,
  Grow,
  CircularProgress,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Json from 'json5';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import ChatIcon from '@mui/icons-material/Chat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LoginModal from './LoginModal';
import { languages, useLanguage, translateFramework, translateFrameworkDescription } from '../data/languages';
import { notifyAuthStateChange } from './UserReadings';

const QuestionContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  padding: theme.spacing(3),
  paddingTop: theme.spacing(16),
  paddingBottom: theme.spacing(8),
  overflow: 'hidden',
}));

const ConversationContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(12),
  width: '100%',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
}));

const QuestionBlock = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(5),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  height: '100%',
  overflow: 'hidden',
}));

const OptionGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  maxWidth: '100%',
  marginLeft: theme.spacing(7), // Align with reader messages (avatar width + margin)
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const OptionChip = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(4),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textAlign: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: "'Space Grotesk', sans-serif",
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

const SearchBox = styled(TextField)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'calc(100% - 32px)',
  maxWidth: '768px',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(4),
  padding: theme.spacing(1, 2),
  zIndex: 100,
  fontFamily: "'Space Grotesk', sans-serif",
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(4),
    fontFamily: "'Space Grotesk', sans-serif",
    '&.Mui-focused': {
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontFamily: "'Space Grotesk', sans-serif",
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.15)',
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  alignSelf: 'flex-start',
  marginBottom: theme.spacing(2),
  color: 'rgba(0, 0, 0, 0.6)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
}));

const BreadcrumbPath = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(8),
  left: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.spacing(3),
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  opacity: 0.9,
  transition: 'all 0.3s ease',
  zIndex: 1200,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  },
}));

const ReaderMessage = styled(Box)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.75)',
  padding: theme.spacing(1, 0),
  maxWidth: 'calc(100% - 50px)',
  alignSelf: 'flex-start',
  marginBottom: theme.spacing(1),
  position: 'relative',
  fontFamily: "'Space Grotesk', sans-serif",
}));

const UserMessage = styled(Box)(({ theme, backgroundColor }) => ({
  backgroundColor: backgroundColor || 'rgba(25, 118, 210, 0.15)',
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2),
  maxWidth: '80%',
  marginLeft: 'auto',
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  position: 'relative',
  zIndex: 100,
}));

const MessageAvatar = styled(Avatar)(({ theme, src }) => ({
  width: 40,
  height: 40,
  marginRight: theme.spacing(1.5),
  backgroundColor: theme.palette.primary.main,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: '2px solid #fff',
}));

const MessageGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2.5),
  paddingLeft: theme.spacing(1),
}));

const SearchSection = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  position: 'absolute',
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  zIndex: 100,
  borderRadius: `0 0 ${theme.spacing(2)}px ${theme.spacing(2)}px`,
  boxShadow: '0 -4px 10px rgba(0,0,0,0.05)',
}));

const ConfirmationOverlay = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  zIndex: 10,
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(1.5, 6),
  borderRadius: theme.spacing(3),
  fontSize: '1.2rem',
  fontFamily: "'Space Grotesk', sans-serif",
  textTransform: 'none',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
  },
  transition: 'all 0.3s ease',
}));

const FrameworkOption = styled(Box)(({ theme, selected }) => ({
  padding: theme.spacing(1.8, 2.2),
  backgroundColor: selected ? 'rgba(25, 118, 210, 0.08)' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.spacing(2.5),
  cursor: 'pointer',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  border: selected ? '2px solid rgba(25, 118, 210, 0.5)' : '1px solid rgba(0, 0, 0, 0.09)',
  boxShadow: selected ? '0 8px 24px rgba(25, 118, 210, 0.15)' : '0 2px 12px rgba(0,0,0,0.05)',
  flex: 1,
  minWidth: '45%',
  maxWidth: '46%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '120px',
  '&:hover': {
    backgroundColor: selected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-4px)',
    boxShadow: selected ? '0 12px 28px rgba(25, 118, 210, 0.2)' : '0 8px 20px rgba(0,0,0,0.1)',
  },
  '&::before': selected ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '6px',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '3px 0 0 3px',
  } : {},
}));

// New component for framework icon
const FrameworkIcon = styled(Box)(({ theme, selected }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: selected ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.03)',
  marginRight: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  color: selected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.6)',
}));

// Slider Track component
const SliderTrack = styled(Box)(({ theme, expanded }) => ({
  width: '52px',
  height: '26px',
  backgroundColor: expanded ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.05)',
  borderRadius: '13px',
  position: 'relative',
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  padding: '0 2px',
}));

// Slider Thumb component
const SliderThumb = styled(Box)(({ theme, expanded }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  position: 'absolute',
  left: expanded ? 'calc(100% - 26px)' : '2px',
  transition: 'left 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ExpandableFramework = ({ framework, index, selected, onClick, currentLang }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      // Add a buffer to ensure text fits properly
      setContentHeight(contentRef.current.scrollHeight + 10);
    }
  }, [framework, expanded]); // Also recalculate when expanded state changes

  // Force recalculation on resize
  useEffect(() => {
    const handleResize = () => {
      if (contentRef.current && expanded) {
        setContentHeight(contentRef.current.scrollHeight + 10);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [expanded]);

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Different icons based on the framework index
  const getIconForIndex = (index) => {
    const icons = [
      <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>★</Box>,
      <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>✦</Box>,
      <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>❖</Box>,
      <Box sx={{ fontSize: '18px', fontWeight: 'bold' }}>⚜</Box>,
    ];
    return icons[index % icons.length];
  };

  return (
    <FrameworkOption
      selected={selected}
      onClick={onClick}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 600,
                color: selected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.85)',
                transition: 'color 0.2s ease',
              }}
            >
              {translateFramework(framework[0], currentLang)}
            </Typography>
          </Box>
          <Box 
            onClick={handleExpandClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SliderTrack expanded={expanded}>
              <SliderThumb expanded={expanded}>
                <FrameworkIcon selected={selected} sx={{ 
                  width: 20, 
                  height: 20, 
                  margin: 0,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}>
                  {getIconForIndex(index)}
                </FrameworkIcon>
              </SliderThumb>
            </SliderTrack>
          </Box>
        </Box>
        
        <Box sx={{ 
          height: expanded ? `${contentHeight}px` : '0px',
          overflow: 'hidden',
          transition: 'height 0.5s ease',
          borderTop: expanded ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
          pt: expanded ? 1.5 : 0,
          mt: expanded ? 1 : 0,
        }}>
          <Typography
            ref={contentRef}
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '0.95rem',
              lineHeight: 1.6,
              color: 'rgba(0, 0, 0, 0.7)',
              opacity: expanded ? 1 : 0,
              transition: 'opacity 0.4s ease',
              display: 'block',
              wordWrap: 'break-word',
              padding: theme.spacing(0, 0.5),
            }}
          >
            {translateFrameworkDescription(framework[1], currentLang)}
          </Typography>
        </Box>
      </Box>
    </FrameworkOption>
  );
};

const MAX_PATH_LENGTH = 2; // Maintain the same path length limit

const LoadingIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(12),
  left: '50%',
  transform: 'translateX(-50%)',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(3),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(5px)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  zIndex: 50,
}));

const hexToRgba = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Add styled components for the inline confidence rating
const InlineSentimentButton = styled(IconButton)(({ theme, selected }) => ({
  padding: theme?.spacing?.(1) || '8px',
  transition: 'all 0.25s ease',
  transform: selected ? 'scale(1.25)' : 'scale(1)',
  opacity: selected ? 1 : 0.6,
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.9,
    transform: selected ? 'scale(1.25)' : 'scale(1.1)',
  }
}));

// Define font size constants for consistency
const FONT_SIZES = {
  large: '1.3rem',
  medium: '1.1rem',
  regular: '1rem',
  small: '0.9rem',
};

export default function QuestionSplit({ 
  selectedNPC, 
  onCardDraw, 
  onInitialChange, 
  isDailyFortune,
  onFortuneSelect,
  language // 接收语言prop
}) {
  const theme = useTheme();
  const location = useLocation();
  const currentLang = language || useLanguage(); // 优先使用传入的language prop
  const config = useMemo(() => languages[currentLang] || languages['en'], [currentLang]); // 优化语言配置获取
  const [path, setPath] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [frameworkContent, setFrameworkContent] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(0);
  const [stressLevel, setStressLevel] = useState(5);
  const [conversation, setConversation] = useState([]);
  const [hasSetConfidence, setHasSetConfidence] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const pendingActionRef = useRef(null);

  // Define the sentiment options for the inline confidence rating
  const sentimentOptions = [
    { value: 1, icon: SentimentVeryDissatisfiedIcon, color: '#d32f2f', label: config.confidenceRating.sentimentLabels[1] },
    { value: 2, icon: SentimentDissatisfiedIcon, color: '#ef6c00', label: config.confidenceRating.sentimentLabels[2] },
    { value: 3, icon: SentimentNeutralIcon, color: '#ffb300', label: config.confidenceRating.sentimentLabels[3] },
    { value: 4, icon: SentimentSatisfiedAltIcon, color: '#689f38', label: config.confidenceRating.sentimentLabels[4] },
    { value: 5, icon: SentimentVerySatisfiedIcon, color: '#2e7d32', label: config.confidenceRating.sentimentLabels[5] }
  ];

  // Get current sentiment based on stress level
  const getCurrentSentiment = () => {
    return sentimentOptions.find(option => option.value === stressLevel) || sentimentOptions[2];
  };

  // 问题翻译映射表 - 集中管理所有问题翻译
  const questionTranslationMap = {
    // The Fool Questions (English to Chinese)
    "What am I secretly awesome at?": "我暗中擅长什么？",
    "How can my skills launch me?": "我的技能如何让我腾飞？",
    "What's holding me back from standing out?": "什么阻碍我脱颖而出？",
    "What should I polish for interviews?": "我应该为面试打磨什么？",
    "Will an offer come knocking soon?": "录用通知很快就会敲门吗？",
    "Which career path fits me best right now?": "哪条职业道路最适合我现在？",
    "How do I transition gracefully?": "怎样优雅地转换职业？",
    "When will love come knocking?": "爱情何时敲门？",
    "Is my ideal partner out there looking for me too?": "我的理想伴侣在寻找我吗？",
    "How can we make our relationship even closer?": "我们如何让关系更加密切？",
    "What would bring fresh energy to our love?": "什么会为我们的爱情注入新的能量？",
    "What should I focus on today?": "今天我应该专注于什么？",
    "What's coming up for me?": "什么即将发生在我身上？",
    
    // The Priestess Questions
    "What talents do you possess that even you have yet to recognize?": "您拥有什么才华，即使您自己也尚未认识到？",
    "Where is your true brilliance hiding, just beneath the surface?": "您的真正才华在哪里隐藏，就在表面之下？",
    "What unseen force is shaping your opportunities?": "什么未被发现的力量在塑造您的机会？",
    "Is an offer already forming, waiting for the right moment?": "录用通知是否已经在形成，等待合适的时刻？",
    
    // Chinese to English (反向映射)
    "我暗中擅长什么？": "What am I secretly awesome at?",
    "我的技能如何让我腾飞？": "How can my skills launch me?",
    "什么阻碍我脱颖而出？": "What's holding me back from standing out?",
    "我应该为面试打磨什么？": "What should I polish for interviews?",
    "录用通知很快就会敲门吗？": "Will an offer come knocking soon?",
    "哪条职业道路最适合我现在？": "Which career path fits me best right now?",
    "怎样优雅地转换职业？": "How do I transition gracefully?",
    "爱情何时敲门？": "When will love come knocking?",
    "我的理想伴侣在寻找我吗？": "Is my ideal partner out there looking for me too?",
    "我们如何让关系更加密切？": "How can we make our relationship even closer?",
    "什么会为我们的爱情注入新的能量？": "What would bring fresh energy to our love?",
    "今天我应该专注于什么？": "What should I focus on today?",
    "什么即将发生在我身上？": "What's coming up for me?",
    "您拥有什么才华，即使您自己也尚未认识到？": "What talents do you possess that even you have yet to recognize?",
    "您的真正才华在哪里隐藏，就在表面之下？": "Where is your true brilliance hiding, just beneath the surface?",
    "什么未被发现的力量在塑造您的机会？": "What unseen force is shaping your opportunities?",
    "录用通知是否已经在形成，等待合适的时刻？": "Is an offer already forming, waiting for the right moment?"
  };

  // 问题翻译器
  const translateQuestion = (questionText) => {
    if (!questionText) return questionText;
    
    // 如果当前是中文，尝试从映射表获取中文版本
    if (currentLang === 'zh') {
      return questionTranslationMap[questionText] || questionText;
    } else {
      // 如果当前是英文，尝试从映射表获取英文版本
      return questionTranslationMap[questionText] || questionText;
    }
  };

  // 响应式消息翻译器
  const translateMessage = (messageKey, params = {}) => {
    // 处理动态消息翻译
    switch (messageKey) {
      case 'DRAWING_CARDS':
        return config.questionSplit.drawingCards;
      case 'GOOD_TO_KNOW':
        return config.questionSplit.goodToKnow;
      case 'WHAT_TO_KNOW':
        return config.questionSplit.whatWouldYouLikeToKnow;
      case 'TROUBLE_CARDS':
        return currentLang === 'zh' ? "卡牌出现了问题。让我们重试。" : "I'm having trouble with the cards. Let's try again.";
      case 'TROUBLE_CONNECTION':
        return currentLang === 'zh' ? "我现在无法连接到卡牌。您能重试吗？" : "I'm having trouble connecting with the cards right now. Could you try again?";
      case 'FRAMEWORK_CONFIRM':
        return currentLang === 'zh' ? `我将使用${params.framework}方法进行您的占卜。` : `I'll use the ${params.framework} approach for your reading.`;
      case 'NODE_GUIDANCE':
        // 对于节点guidance，如果有nodeGuidance则使用，否则回退到默认消息
        return params.nodeGuidance || config.questionSplit.whatWouldYouLikeToKnow;
      case 'GOOD_TO_KNOW_GUIDANCE':
        return `${config.questionSplit.goodToKnow} ${params.nodeGuidance || config.questionSplit.whatWouldYouLikeToKnow}`;
      default:
        return messageKey; // 返回原始文本作为fallback
    }
  };

  // 选项翻译映射 - 为系统选项提供翻译
  const choiceTranslationMap = {
    // 英文到中文
    "Career": "职业",
    "Love": "爱情", 
    "Health": "健康",
    "Finance": "财务",
    "Family": "家庭",
    "Friendship": "友谊", 
    "Personal Growth": "个人成长",
    "What will happen if...": "如果...会发生什么",
    "Should I start...": "我应该开始...",
    "How can I improve...": "我如何改善...",
    "When will...": "何时会...",
    "Why do I feel...": "为什么我感到...",
    "What should I do about...": "我应该怎么处理...",
    
    // 中文到英文（反向映射）
    "职业": "Career",
    "爱情": "Love",
    "健康": "Health", 
    "财务": "Finance",
    "家庭": "Family",
    "友谊": "Friendship",
    "个人成长": "Personal Growth",
    "如果...会发生什么": "What will happen if...",
    "我应该开始...": "Should I start...",
    "我如何改善...": "How can I improve...",
    "何时会...": "When will...",
    "为什么我感到...": "Why do I feel...",
    "我应该怎么处理...": "What should I do about..."
  };

  // 选项翻译器
  const translateChoice = (choice) => {
    if (!choice) return choice;
    return choiceTranslationMap[choice] || choice;
  };

  // 获取角色对应的翻译内容
  const getCharacterTranslatedContent = (field) => {
    if (!selectedNPC) return '';
    
    // 根据selectedNPC.id获取对应的角色key
    const characterKeys = {
      1: 'the_fool',
      2: 'the_hanged_man', 
      3: 'the_magician',
      4: 'the_priestess'
    };
    
    const characterKey = characterKeys[selectedNPC.id];
    if (!characterKey || !config.characters[characterKey]) {
      return selectedNPC[field] || '';
    }
    
    return config.characters[characterKey][field] || selectedNPC[field] || '';
  };

  // 获取emotion_guidence的便利方法
  const getEmotionGuidance = () => getCharacterTranslatedContent('emotion_guidence');

  // 路径项目直接显示，无需翻译（已经是正确语言）

  // 语言感知的数据加载器
  const loadCharacterData = async (npcInfo, importName, language) => {
    try {
      console.log(`Loading character data: ${npcInfo}, language: ${language}`); // 调试日志
      const module = await import(`../data/${npcInfo}`);
      
      // 检查是否有动态语言函数（新格式）
      if (module[`get${importName.charAt(0).toUpperCase()}${importName.slice(1)}`]) {
        const getDataFn = module[`get${importName.charAt(0).toUpperCase()}${importName.slice(1)}`];
        const data = getDataFn(language);
        console.log(`Loaded data choices:`, Object.keys(data.choices || {})); // 调试日志
        return data;
      }
      
      // 回退到静态导出（旧格式）
      return module[importName];
    } catch (error) {
      console.error("Error loading character data:", error);
      return null;
    }
  };

  useEffect(() => {
    if (selectedNPC?.info) {
      loadCharacterData(selectedNPC.info, selectedNPC.import_name, currentLang)
        .then(npcData => {
          if (npcData) {
            setCurrentNode(npcData);
            
            // Initialize conversation with greeting and confidence component directly
            setConversation([
              {
                type: 'reader',
                content: getEmotionGuidance(),
                showConfidenceRating: true, // Flag to show the confidence rating directly
                isTranslatable: true // 现在支持翻译
              }
            ]);
          }
        });
    }
  }, [selectedNPC, currentLang]); // 添加currentLang依赖

  // 语言切换时重新加载数据（清理状态避免混合语言）
  useEffect(() => {
    if (conversation.length > 1 && currentNode) { // 只有超过1条消息才清理
      // 重置状态以避免语言混合问题
      setPath([]);
      setSelectedQuestion(null);
      setShowConfirmation(false);
      setFrameworkContent([]);
      setConversation([
        {
          type: 'reader',
          content: getEmotionGuidance(),
          showConfidenceRating: true,
          isTranslatable: true
        }
      ]);
      setHasSetConfidence(false); // 重置信心设置状态
    }
  }, [currentLang]); // 只在语言变化时清理状态

  const setLevel = (newValue) => {
    setStressLevel(newValue);
    if (onInitialChange) {
      onInitialChange(newValue);
    }
  };

  const getCurrentNode = (node, path) => {
    let current = node;
    for (const key of path) {
      current = current.choices[key];
    }
    return current;
  };

  const handleBack = () => {
    if (showConfirmation) {
      // When going back from confirmation, just hide confirmation but keep the question selected
      // This allows the user to see the question options again and potentially select a different one
      setShowConfirmation(false);
      
      // Remove both the framework suggestion message and the user's question from conversation
      const newConversation = [...conversation];
      // Remove the last reader message (framework suggestion) if it exists
      if (newConversation.length > 0 && newConversation[newConversation.length - 1].type === 'reader') {
        newConversation.pop();
      }
      // Also remove the user's question message
      if (newConversation.length > 0 && newConversation[newConversation.length - 1].type === 'user') {
        newConversation.pop();
      }
      setConversation(newConversation);
      
      // Keep selectedQuestion so the previously selected option remains highlighted
      // but clear the framework content
      setFrameworkContent([]);
    } else if (path.length > 0) {
      // Remove the last path segment
      const newPath = path.slice(0, -1);
      setPath(newPath);
      setSelectedQuestion(null);
      
      // Find the index of the last topic selection in the conversation
      let lastTopicIndex = -1;
      for (let i = conversation.length - 1; i >= 0; i--) {
        if (conversation[i].type === 'user' && conversation[i].content === path[path.length - 1]) {
          lastTopicIndex = i;
          break;
        }
      }
      
      // If found, remove all messages after that point
      if (lastTopicIndex >= 0) {
        setConversation(conversation.slice(0, lastTopicIndex));
      } else {
        // Fallback: just remove the last two messages
        const newConversation = [...conversation];
        // Remove the last two messages (user choice and reader response)
        if (newConversation.length > 0) newConversation.pop();
        if (newConversation.length > 0) newConversation.pop();
        setConversation(newConversation);
      }
    }
  };

  const scrollToLatest = () => {
    // Find the conversation container
    const container = document.querySelector('.conversation-container');
    if (container) {
      // Calculate the height of the last user message
      const lastUserMessage = container.querySelector('.user-message:last-child');
      if (lastUserMessage) {
        const messageHeight = lastUserMessage.offsetHeight;
        // Scroll to position the last user message at the top
        container.scrollTo({
          top: container.scrollHeight - container.clientHeight - messageHeight,
          behavior: 'smooth'
        });
      } else {
        // Fallback to scrolling to bottom
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleChoiceClick = (choice) => {
    // Add user's choice to conversation
    setConversation(prev => [
      ...prev, 
      { 
        type: 'user', 
        content: choice, 
        isNew: true,
        isTranslatable: false // 用户选择不需要翻译，显示当前语言的选项
      }
    ]);
    
    const nextNode = getCurrentNode(currentNode, [...path, choice]);
    setPath([...path, choice]);
    
    // Add reader's response to conversation with reduced delay
    setTimeout(() => {
      setConversation(prev => [
        ...prev, 
        { 
          type: 'reader', 
          content: translateMessage('NODE_GUIDANCE', { nodeGuidance: nextNode.guidance }),
          messageKey: 'NODE_GUIDANCE',
          nodeGuidance: nextNode.guidance, // 保存节点guidance以便翻译
          isNew: true,
          isTranslatable: true
        }
      ]);
      
      // Scroll to position the last user message at the top
      setTimeout(() => {
        scrollToLatest();
      }, 50);
      
      // Mark messages as not new after a shorter animation
      setTimeout(() => {
        setConversation(prev => 
          prev.map(msg => ({ ...msg, isNew: false }))
        );
      }, 300);
    }, 300);
  };

  const handleQuestionSelect = async (question, searchText) => {
    // Don't allow selecting a new question while loading
    if (isLoading) return;
    
    // 处理问题对象和文本
    const questionText = typeof question === 'object' ? question.text : question;
    const questionId = typeof question === 'object' ? question.id : null;
    
    // Add user's question to conversation with translation support
    setConversation(prev => [
      ...prev, 
      { 
        type: 'user', 
        content: searchText ? searchText : questionText, 
        originalQuestionText: questionText, // 存储原始问题文本用于翻译
        questionId: questionId, // 存储问题ID（如果有）
        isQuestionMessage: !!questionText && !searchText, // 只有预定义问题才可翻译
        isTranslatable: !!questionText && !searchText, // 用户自定义问题不翻译
        isNew: true 
      }
    ]);
    
    setSelectedQuestion(questionText);
    setIsLoading(true);
    
    try {
      // Fetch framework data
      const response = await fetch('/framework?' + new URLSearchParams({
        question: questionText, // 使用questionText而不是question对象
      }).toString());
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      const contentArray = data.content;
      
      // Scroll to position the last user message at the top
      setTimeout(() => {
        scrollToLatest();
      }, 50);
      
      setFrameworkContent(contentArray);
      setSelectedFramework(0);
      
      // Mark messages as not new after a shorter animation
      setTimeout(() => {
        setConversation(prev => 
          prev.map(msg => ({ ...msg, isNew: false }))
        );
      }, 300);
      
      setShowConfirmation(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching framework:', error);
      setFrameworkContent([]);
      
      // Add error message
      setConversation(prev => [
        ...prev,
        { 
          type: 'reader', 
          content: translateMessage('TROUBLE_CONNECTION'), 
          messageKey: 'TROUBLE_CONNECTION',
          isNew: true,
          isTranslatable: true
        }
      ]);
      
      // Scroll to position the last user message at the top
      setTimeout(() => {
        scrollToLatest();
      }, 50);
      
      setIsLoading(false);
    }
  };

  // 处理登录模态框
  const handleLoginModalOpen = (action) => {
    pendingActionRef.current = action;
    setLoginModalOpen(true);
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
    // 执行待处理的操作
    if (pendingActionRef.current) {
      pendingActionRef.current();
    }
  };

  const handleUserLogin = (userData) => {
    // 触发身份验证状态更改通知以更新所有组件
    notifyAuthStateChange();
    handleLoginModalClose();
  };

  // 修改 drawThreeWithAPI 函数以包含登录检查
  const drawThreeWithAPI = (questionText, stressLevel) => {
    // 检查用户是否已登录
    const user = localStorage.getItem('user');
    if (!user) {
      // 如果用户未登录，打开登录模态框
      handleLoginModalOpen(() => drawThreeWithAPI(questionText, stressLevel));
      return;
    }

    setIsDrawing(true);

    // 添加抽牌消息
    setConversation(prev => [
      ...prev,
      {
        type: 'reader',
        content: translateMessage('DRAWING_CARDS'),
        messageKey: 'DRAWING_CARDS',
        isNew: true,
        isTranslatable: true
      }
    ]);

    console.log("questionText", questionText);
    console.log("Calling /draw endpoint...");

    // 使用正确的 API 端点
    fetch('/draw?' + new URLSearchParams({
      count: 3,
    }).toString())
      .then(response => {
        console.log("Draw response status:", response.status);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log("Draw response data:", data);
        console.log("Calling onCardDraw with:", {
          framework: frameworkContent[selectedFramework],
          selectedQuestion: questionText,
          cards: data.cards,
          cardOne: data.cardone,
          cardTwo: data.cardtwo,
          cardThree: data.cardthree,
          initialStressLevel: stressLevel,
        });

        onCardDraw({
          framework: frameworkContent[selectedFramework]?.[0] || frameworkContent[selectedFramework], // Pass just the framework string, not the array
          selectedQuestion: questionText,
          cards: data.cards,
          cardOne: data.cardone,
          cardTwo: data.cardtwo,
          cardThree: data.cardthree,
          initialStressLevel: stressLevel,
        });
      })
      .catch(error => {
        console.error('Error drawing cards:', error);
        setIsDrawing(false);

        setConversation(prev => [
          ...prev,
          {
            type: 'reader',
            content: translateMessage('TROUBLE_CARDS'),
            messageKey: 'TROUBLE_CARDS',
            isNew: true,
            isTranslatable: true
          }
        ]);
      });
  };

  // 修改 handleFortuneTypeSelect 以包含登录检查
  const handleFortuneTypeSelect = (type) => {
    // 检查用户是否已登录
    const user = localStorage.getItem('user');
    if (!user) {
      // 如果用户未登录，打开登录模态框
      handleLoginModalOpen(() => handleFortuneTypeSelect(type));
      return;
    }

    if (onFortuneSelect) {
      onFortuneSelect(type);
    }
  };

  const handleSearchSubmit = () => {
    if (searchText.trim()) {
      const pathContext = getPathText(path);
      const fullQuestion = pathContext ? 
        `[Context: ${pathContext}] ${searchText.trim()}` : 
        searchText.trim();
      
      handleQuestionSelect(fullQuestion, searchText.trim());
      setSearchText('');
    }
  };

  const handleConfirm = () => {
    // Add confirmation message
    setConversation(prev => [
      ...prev, 
      { 
        type: 'reader', 
        content: translateMessage('FRAMEWORK_CONFIRM', { framework: translateFramework(frameworkContent[selectedFramework][0], currentLang) }), 
        messageKey: 'FRAMEWORK_CONFIRM',
        params: { framework: translateFramework(frameworkContent[selectedFramework][0], currentLang) },
        isNew: true,
        isTranslatable: true
      }
    ]);
    
    drawThreeWithAPI(selectedQuestion, stressLevel); // 使用已定义的selectedQuestion状态
    setShowConfirmation(false);
  };

  // Update handleConfidenceSubmit to trigger confetti
  const handleConfidenceSubmit = () => {
    setHasSetConfidence(true);
    if (onInitialChange) {
      onInitialChange(stressLevel);
    }
    
    // Get the current sentiment for the icon
    const currentSentiment = getCurrentSentiment();
    
    // Add a message with the selected icon and the next reader message
    setConversation(prev => [
      ...prev,
      {
        type: 'user',
        showSelectedIcon: true, // Flag to show icon instead of text
        selectedValue: stressLevel, // Store selected value
        isNew: true
      },
      {
        type: 'reader',
        content: translateMessage('GOOD_TO_KNOW_GUIDANCE', { nodeGuidance: currentNode?.guidance }),
        messageKey: 'GOOD_TO_KNOW_GUIDANCE',
        nodeGuidance: currentNode?.guidance,
        isNew: true,
        isTranslatable: true
      }
    ]);
  };

  // Render the daily/weekly selection UI
  const renderDailyWeeklySelection = () => {
    // Get the NPC's colors with fallbacks
    const npcBackgroundColor = selectedNPC?.dialogue_background_color || theme.palette.primary.main;
    const npcTextColor = selectedNPC?.dialogue_text_color || '#ffffff';
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        padding: theme.spacing(2, 4),
        boxSizing: 'border-box',
        alignItems: 'center',
      }}>
        {/* Header section with avatar and name */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(2),
          width: '100%',
          maxWidth: '700px',
        }}>
          <Avatar 
            src={selectedNPC?.avatar_image}
            sx={{
              width: 60,
              height: 60,
              border: '3px solid white',
              boxShadow: '0 4px 15px rgba(0,0,0,0.12)',
              mr: 2,
              flexShrink: 0,
            }}
          >
            {!selectedNPC?.avatar_image && (selectedNPC?.name?.charAt(0) || 'T')}
          </Avatar>
          
          <Box sx={{ 
            flex: 1, 
            maxWidth: 'calc(100% - 80px)',
          }}>
            <Typography variant="h4" sx={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              color: theme.palette.primary.main,
              mb: 0.2,
              fontSize: '1.5rem',
            }}>
{getCharacterTranslatedContent('title') || (currentLang === 'zh' ? '您的塔罗师' : 'Tarot Reader')}
            </Typography>
            <Typography variant="subtitle1" sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'rgba(0, 0, 0, 0.7)',
              fontSize: '0.85rem',
            }}>
{getCharacterTranslatedContent('subtitle') || config.questionSplit.mysticGuide}
            </Typography>
          </Box>
        </Box>
        
        {/* Content container for better centering */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '700px',
          flex: 1,
        }}>
          {/* Fortune dialogue from NPC */}
          <Box sx={{
            padding: 2,
            backgroundColor: hexToRgba(npcBackgroundColor, 0.08),
            borderRadius: 2,
            marginBottom: 2,
            border: `1px solid ${hexToRgba(npcBackgroundColor, 0.2)}`,
            width: '100%',
            textAlign: 'center',
          }}>
            <Typography sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '1rem',
              fontWeight: 500,
              lineHeight: 1.5,
              color: 'rgba(0, 0, 0, 0.8)',
              fontStyle: 'italic',
            }}>
{getCharacterTranslatedContent('fortune_dialogue') || (currentLang === 'zh' ? '我将引导您进行运势占卜。' : 'I will guide you through a fortune reading.')}
            </Typography>
          </Box>
          
          {/* Main content area */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            justifyContent: 'left',
            position: 'relative',
            width: '100%',
          }}>
            {/* Main content container */}
            <Box sx={{
              width: '100%',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}>
              {/* Dialogue-style guidance */}
              <Box sx={{
                mb: 3,
                position: 'relative',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: 2,
                padding: 2,
                width: '90%',
                maxWidth: '600px',
              }}>
                <Typography sx={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: 'rgba(0, 0, 0, 0.9)',
                  textAlign: 'center',
                }}>
{getCharacterTranslatedContent('fortune_guidence') || config.questionSplit.chooseReadingType}
                </Typography>
              </Box>
              
              {/* Reading type selection cards */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'row',
                gap: 4,
                width: '100%',
                textAlign: 'left',
                justifyContent: 'left',
                alignItems: 'stretch',
                position: 'relative',
                mt: 4,
                mb: 3,
              }}>
                {/* Daily Reading Card */}
                <Box sx={{
                  flex: '1 1 0',
                  maxWidth: '240px',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  }
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleFortuneTypeSelect('daily')}
                    fullWidth
                    sx={{
                      py: 2,
                      px: 2,
                      height: '100%',
                      minHeight: '160px',
                      borderRadius: 2,
                      borderColor: npcBackgroundColor,
                      borderWidth: 2,
                      color: 'rgba(0,0,0,0.87)',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(5px)',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.06)',
                      '&:hover': {
                        borderColor: npcBackgroundColor,
                        background: 'rgba(255, 255, 255, 0.98)',
                        boxShadow: `0 8px 20px ${hexToRgba(npcBackgroundColor, 0.2)}`,
                      },
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ 
                      fontSize: '2.2rem', 
                      mb: 0.5,
                      color: npcBackgroundColor
                    }}>🌅</Box>
                    <Box sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{config.questionSplit.dailyReading}</Box>
                    <Typography sx={{ 
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: 'rgba(0,0,0,0.6)',
                      mt: 0.5,
                      maxWidth: '90%',
                      textAlign: 'center',
                      lineHeight: 1.4,
                    }}>
                      {config.questionSplit.discoverYourPath}
                    </Typography>
                  </Button>
                </Box>

                {/* Weekly Reading Card */}
                <Box sx={{
                  flex: '1 1 0',
                  maxWidth: '240px',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  }
                }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleFortuneTypeSelect('weekly')}
                    fullWidth
                    sx={{
                      py: 2,
                      px: 2,
                      height: '100%',
                      minHeight: '160px',
                      borderRadius: 2,
                      borderColor: npcBackgroundColor,
                      borderWidth: 2,
                      color: 'rgba(0,0,0,0.87)',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(5px)',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.06)',
                      '&:hover': {
                        borderColor: npcBackgroundColor,
                        background: 'rgba(255, 255, 255, 0.98)',
                        boxShadow: `0 8px 20px ${hexToRgba(npcBackgroundColor, 0.2)}`,
                      },
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ 
                      fontSize: '2.2rem', 
                      mb: 0.5,
                      color: npcBackgroundColor
                    }}>📅</Box>
                    <Box sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{config.questionSplit.weeklyReading}</Box>
                    <Typography sx={{ 
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: 'rgba(0,0,0,0.6)',
                      mt: 0.5,
                      maxWidth: '90%',
                      textAlign: 'center',
                      lineHeight: 1.4,
                    }}>
                      {config.questionSplit.planYourWeek}
                    </Typography>
                  </Button>
                </Box>
              </Box>
              
              {/* Footer note */}
              <Typography sx={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '0.8rem',
                color: 'rgba(0, 0, 0, 0.5)',
                textAlign: 'left',
                width: '100%',
                mt: 2,
                mb: 3,
                padding: theme.spacing(0.5),
                position: 'relative',
                zIndex: 2,
                alignItems: 'left',
              }}>
                {config.questionSplit.bothReadingsNote}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (!currentNode) return null;

  const node = getCurrentNode(currentNode, path);

  // Helper function to get the current path as text
  const getPathText = (path) => {
    if (!path.length) return '';
    return path.join(' , ');
  };

  // Add a function to determine if a message is the latest reader message
  const isLatestReaderMessage = (index) => {
    // Start from the end of the conversation and find the first reader message
    for (let i = conversation.length - 1; i >= 0; i--) {
      if (conversation[i].type === 'reader') {
        return i === index;
      }
    }
    return false;
  };

  // Update the renderMessage function to handle showing the icon
  const renderMessage = (message, index) => {
    const isLatestMessage = index === conversation.length - 1;

    if (message.type === 'user') {
      const backgroundColor = selectedNPC?.dialogue_background_color ? 
        hexToRgba(selectedNPC.dialogue_background_color, 0.15) : 
        'rgba(25, 118, 210, 0.15)';

      // Special case for confidence icon message
      if (message.showSelectedIcon) {
        // Find the sentiment option for the selected value
        const selectedSentiment = sentimentOptions.find(option => 
          option.value === message.selectedValue
        ) || sentimentOptions[2];
        
        const IconComponent = selectedSentiment.icon;
        
        return (
          <Fade
            key={index}
            in={true}
            timeout={800}
            easing={{
              enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <UserMessage backgroundColor={backgroundColor}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center', 
                gap: 1.5 
              }}>
                <IconComponent 
                  sx={{ 
                    fontSize: 32,
                    color: selectedSentiment.color
                  }} 
                />
                <Typography sx={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: FONT_SIZES.medium,
                  fontWeight: 500,
                  color: selectedSentiment.color,
                }}>
                  {selectedSentiment.label}
                </Typography>
              </Box>
            </UserMessage>
          </Fade>
        );
      }

      // Standard user message
      return (
        <Fade
          key={index}
          in={true}
          timeout={800}
          easing={{
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <UserMessage backgroundColor={backgroundColor}>
            <Typography sx={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: isLatestMessage ? FONT_SIZES.medium : FONT_SIZES.regular,
              lineHeight: 1.6,
              fontWeight: isLatestMessage ? 600 : 400,
              color: isLatestMessage ? 'rgba(0, 0, 0, 0.95)' : 'rgba(0, 0, 0, 0.75)',
            }}>
              {message.content}
            </Typography>
          </UserMessage>
        </Fade>
      );
    } else {
      // Reader message (unchanged)
      return (
        <Grow
          key={index}
          in={true}
          timeout={1200}
          style={{ 
            transformOrigin: '0 0 0',
            transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <MessageGroup>
            {isLatestReaderMessage(index) && (
              <MessageAvatar src={selectedNPC?.avatar_image}>
                {!selectedNPC?.avatar_image && (selectedNPC?.name?.charAt(0) || 'T')}
              </MessageAvatar>
            )}
            <ReaderMessage sx={{ 
              maxWidth: isLatestReaderMessage(index) ? 'calc(100% - 50px)' : '100%',
              ml: isLatestReaderMessage(index) ? 0 : 7
            }}>
              <Typography sx={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: isLatestMessage ? FONT_SIZES.medium : FONT_SIZES.regular,
                lineHeight: 1.6,
                fontWeight: isLatestMessage ? 500 : 400,
                color: isLatestMessage ? 'rgba(0, 0, 0, 0.95)' : 'rgba(0, 0, 0, 0.75)',
              }}>
                {message.content}
              </Typography>
            </ReaderMessage>
          </MessageGroup>
        </Grow>
      );
    }
  };

  // Render inline confidence rating
  const renderInlineConfidenceRating = () => {
    const currentSentiment = getCurrentSentiment();
    
    return (
      <Box sx={{ mt: 1 }}>
        {/* Display sentiment text */}
        <Typography sx={{ 
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: FONT_SIZES.medium,
          color: currentSentiment.color,
          fontWeight: 500,
          ml: 0.5,
          mb: 1.5
        }}>
          {currentSentiment.label}
        </Typography>
        
        {/* Sentiment icons row */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '400px',
          mb: 2
        }}>
          {sentimentOptions.map((option) => {
            const IconComponent = option.icon;
            const isSelected = stressLevel === option.value;
            
            return (
              <InlineSentimentButton 
                key={option.value}
                selected={isSelected}
                onClick={() => setLevel(option.value)}
                disableRipple
              >
                <IconComponent 
                  sx={{ 
                    fontSize: 36,
                    color: option.color,
                  }} 
                />
              </InlineSentimentButton>
            );
          })}
        </Box>
        
        {/* Continue button - use regular Button with sx props instead of styled component */}
        <Button
          variant="contained"
          onClick={handleConfidenceSubmit}
          sx={{
            mt: 2,
            borderRadius: 3,
            px: 4,
            py: 1,
            fontSize: FONT_SIZES.regular,
            fontFamily: "'Space Grotesk', sans-serif",
            textTransform: 'none',
            backgroundColor: currentSentiment.color,
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: `${currentSentiment.color}dd`,
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
            transition: 'all 0.3s ease',
          }}
        >
{config.questionSplit.continue}
        </Button>
      </Box>
    );
  };

  return (
    <QuestionContainer>
      {/* Only show breadcrumb path if not in daily fortune mode */}
      {!isDailyFortune && (
        <BreadcrumbPath>
          <IconButton 
            onClick={handleBack}
            disabled={path.length === 0 && !showConfirmation}
            sx={{ 
              color: 'rgba(0, 0, 0, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
              p: 0.5,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          {path.length > 0 ? (
            <>
              {path.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Typography 
                      sx={{ 
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: '0.9rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      /
                    </Typography>
                  )}
                  <Typography 
                    key={index}
                    sx={{ 
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1.1rem',
                      color: index === path.length - 1 ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.6)',
                      fontWeight: index === path.length - 1 ? 500 : 400,
                    }}
                  >
                    {item}
                  </Typography>
                </React.Fragment>
              ))}
            </>
          ) : (
            <Typography sx={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '1.1rem',
            }}>
              {selectedNPC?.name || (currentLang === 'zh' ? '塔罗占卜' : 'Tarot Reading')}
            </Typography>
          )}
        </BreadcrumbPath>
      )}

      {/* If it's daily fortune mode, show the daily/weekly selection directly in the container */}
      {isDailyFortune ? renderDailyWeeklySelection() : (
        <QuestionBlock elevation={0}>
          <ConversationContainer className="conversation-container">
            {conversation.map((message, index) => (
              <React.Fragment key={index}>
                {renderMessage(message, index)}
                
                {/* Special case: render inline confidence rating after first message if needed */}
                {index === 0 && message.showConfidenceRating && !hasSetConfidence && (
                  <Grow
                    in={true}
                    timeout={1200}
                    style={{ 
                      transformOrigin: '0 0 0',
                      transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                  >
                    <Box sx={{ 
                      ml: 7, 
                      width: 'calc(100% - 50px)',
                      maxWidth: 'calc(100% - 50px)',
                      mt: 1.5,
                      mb: 3
                    }}>
                      {renderInlineConfidenceRating()}
                    </Box>
                  </Grow>
                )}
              </React.Fragment>
            ))}

            {/* Show options if we're at a node with choices AND confidence has been set */}
            {hasSetConfidence && node.choices && !showConfirmation && (
              <>
                <MessageGroup>
                  <Box sx={{ width: '100%' }}>
                    <OptionGrid>
                      {Object.keys(node.choices).map((choice) => (
                        <OptionChip
                          key={choice}
                          onClick={() => handleChoiceClick(choice)}
                          sx={{
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            border: '1px solid',
                          }}
                        >
                          <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            {choice}
                          </Typography>
                        </OptionChip>
                      ))}
                    </OptionGrid>
                  </Box>
                </MessageGroup>
              </>
            )}

            {/* Show question options if we're at a leaf node, confidence is set, not loading, and not in confirmation mode */}
            {hasSetConfidence && !node.choices && node.questions && !showConfirmation && !isLoading && (
              <>
                <MessageGroup>
                  <Box sx={{ width: '100%' }}>
                    <OptionGrid>
                      {node.questions.map((question, index) => {
                        const questionText = typeof question === 'object' ? question.text : question;
                        const questionId = typeof question === 'object' ? question.id : null;
                        
                        return (
                          <OptionChip
                            key={questionId || index}
                            onClick={() => handleQuestionSelect(question, "")}
                            sx={{
                              borderColor: selectedQuestion === questionText ? 'rgba(25, 118, 210, 0.5)' : 'transparent',
                              backgroundColor: selectedQuestion === questionText ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                            }}
                          >
                            <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                              {questionText}
                            </Typography>
                          </OptionChip>
                        );
                      })}
                    </OptionGrid>
                  </Box>
                </MessageGroup>
              </>
            )}
            
            {/* Show confirmation UI as part of the conversation when needed */}
            {showConfirmation && (
              <Grow in={true} timeout={1200} style={{ transformOrigin: '0 0 0' }}>
                <MessageGroup>
                  <MessageAvatar src={selectedNPC?.avatar_image}>
                    {!selectedNPC?.avatar_image && (selectedNPC?.name?.charAt(0) || 'T')}
                  </MessageAvatar>
                  <ReaderMessage sx={{ maxWidth: '95%' }}>
                    <Typography
                      sx={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: '1.2rem',
                        mb: 3,
                        fontWeight: 500,
                      }}
                    >
                      {config.questionSplit.chooseFramework}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2.5,
                      mt: 3,
                      mb: 2,
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'stretch', // Ensure all items stretch to match tallest
                    }}>
                      {frameworkContent.map((framework, index) => (
                        <ExpandableFramework
                          key={index}
                          framework={framework}
                          index={index}
                          selected={index === selectedFramework}
                          onClick={() => setSelectedFramework(index)}
                          currentLang={currentLang}
                        />
                      ))}
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mt: 5,
                    }}>
                      <ConfirmButton
                        onClick={handleConfirm}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          minWidth: '200px',
                        }}
                      >
                        {config.questionSplit.beginReading}
                      </ConfirmButton>
                    </Box>
                  </ReaderMessage>
                </MessageGroup>
              </Grow>
            )}
          </ConversationContainer>
          
          {/* Loading indicator */}
          {isLoading && (
            <LoadingIndicator>
              <Typography sx={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '1rem',
              }}>
                {config.questionSplit.consultingCards}
              </Typography>
              <Box sx={{ 
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                animation: 'pulse 1.5s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%': { opacity: 0.4, transform: 'scale(0.8)' },
                  '50%': { opacity: 1, transform: 'scale(1.2)' },
                  '100%': { opacity: 0.4, transform: 'scale(0.8)' },
                }
              }} />
            </LoadingIndicator>
          )}
        </QuestionBlock>
      )}
      
      {/* Only show search box when not in daily fortune mode */}
      {!isDailyFortune && hasSetConfidence && (
        <SearchBox
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={config.questionSplit.askYourOwnQuestion}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearchSubmit();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSearchSubmit}
                  disabled={isDrawing || !searchText.trim()}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* 添加登录模态框 */}
      <LoginModal 
        open={loginModalOpen} 
        onClose={handleLoginModalClose} 
        onUserLogin={handleUserLogin}
      />
    </QuestionContainer>
  );
}